import $ from '@sparta.global.vendors/jquery';
import spartaGlobalFunctions from '@sparta.global.utilities/sparta-global-functions-utility/dist/es6.js';
import spartaStoreUtility from '@sparta.global.utilities/sparta-store-utility/dist/es6.js';
import spartaValidationFramework from '@sparta.global.utilities/sparta-validation-framework-utility/dist/es6.js';
import spartaInputFormattingUtility from '@sparta.global.utilities/sparta-input-formatting-utility/dist/es6.js';
import spartaInputAdaUtility from '@sparta.global.utilities/sparta-input-ada-utility/dist/es6.js';
import spartaMaskingUtility from '@sparta.global.utilities/sparta-masking-utility/dist/es6.js';

/* eslint-disable valid-jsdoc */

/**
   * @method getHiddenElementDimensions
   * @param {HTMLElement} element -The target element to measure.
   * @description -Measures the dimensions of a hidden element by cloning it
   * and applying the same computed styles.
   * @returns {Object|null} - An object containing the width and height, or null if the element is not provided.
   */
function getHiddenElementDimensions(element) {
  // get computed styles of original element
  const computedStyles = window.getComputedStyle(element);

  // get temporary clone of the element
  const clone = element.cloneNode(true);

  //set style to make element invisible and off-screen
  Object.assign(clone.style, {
    position: 'absolute',
    visibility: 'hidden',
    display: 'block',
    opacity: '0',
    pointerEvents: 'none',
    overflow: 'visible',
    height: computedStyles.height,
    boxSizing: computedStyles.boxSizing,
    font: computedStyles.font,
    paddingLeft: computedStyles.paddingLeft,
    paddingRight: computedStyles.paddingRight,
    paddingTop: computedStyles.paddingTop,
    paddingBottom: computedStyles.paddingBottom
  });

  //append clone to body
  document.body.appendChild(clone);

  //get the dimensions after applying styles
  const dimensions = {
    width: clone.offsetWidth,
    height: clone.offsetHeight
  };

  //remove the clone from DOM
  document.body.removeChild(clone);
  return dimensions;
}

/**
 * @method updateCurrencySymbol
 * @param input target input that currency symbol needs to be updated
 * @param currencyType currencytype value that need to be updated
 * @description find inputs within scope and set currency symbol as prefix as well as for ADA
 */
function updateCurrencySymbol(inputValue, currencyType) {
  const currencySymbolADA = {};
  // Pull in spa params
  const lang = spartaGlobalFunctions.getLanguage();
  const spaParam = spartaGlobalFunctions.getSpaParams();
  const currencyGlobalParams = spaParam.global[lang].inputs.currency;
  Object.assign(currencySymbolADA, {
    currencySymbolADAText: currencyGlobalParams.currencySymbolADAText
  });
  const inputBox = inputValue.closest('.spa-input-box');
  const currencyAda = inputBox.querySelector('.spa-input-text-currency-ada');
  const currencyValue = inputBox.querySelector('.spa-input-prefix--currency');
  const updatedAriaLabel = `${currencySymbolADA.currencySymbolADAText.replace('[currencySymbol]', currencyType)}`.trim();
  currencyValue.innerHTML = currencyType;
  currencyAda.innerHTML = updatedAriaLabel;
  currencyValue.style.width = ''; //reset width so we can get it anew
  const currencyWidth = getHiddenElementDimensions(currencyValue).width;
  currencyValue.style.width = `${currencyWidth + 9}px`;
  currencyValue.setAttribute('data-currency-value', currencyType);
  inputValue.style.paddingLeft = `${currencyWidth + 9}px`;
}

/**
 * @class SpartaInputUtility
 * @description
 *  configure inputs for validation, formatting, obfuscation, etc.
 */
class SpartaInputUtility {
  /**
   * @method constructor
   * @param {String} selector query selector string for the form that we will be initializing
   * @param {Object} [settings={}] Optional Object for configuring overrides
   * @description initializes new instance of SpartaInputUtility Class
   */
  constructor(selector, settings = {}) {
    // get mobile browser setting from Store
    this.isMobile = spartaStoreUtility.getData('browser').mobile;

    // extend options with provided settings
    this.options = Object.assign({}, settings);

    // get application container from store
    this.applicationContainer = spartaStoreUtility.getData('applicationContainer', this.options.widgetID && {
      id: this.options.widgetID
    });

    // Kill check if application container isn't found
    if (!this.applicationContainer) {
      this.applicationContainer = document;
    }
    this.inputs = [];
    this.optionInputs = [];
    this.counterOffset = 11;

    /*
      NOTE: to maximize compatibility with previous versions of this utility
      we accept either a query selector string, jQuery Object, or HTML element
      for the selector parameter, but a simple query string is preferred
    */

    // set instance scope based on selector argument
    // check to see if we were given an HTML Node
    if (!selector.nodeName) {
      // check to see if we were given a jQuery Object
      if (!selector[0]) {
        this.scope = document.querySelector(selector);
      } else {
        this.scope = selector[0];
      }
    } else {
      this.scope = selector;
    }

    // initialize the validation framework
    spartaValidationFramework.init();

    // initialize instance
    this.init(this.options.autoMask, this.options.widgetID);
  }

  /**
   * @method init
   * @description initialize utility on desired inputs
   */
  init(autoMask = false, widgetID = '') {
    if (!this.globalParams) {
      this.language = spartaGlobalFunctions.getLanguage();
      this.globalParams = spartaGlobalFunctions.getSpaParams().global[this.language];
      this.inputText = this.globalParams.inputs;
    }

    // get list of inputs
    this.getInputs();
    this.getInputCheckboxes();

    // get list of options
    this.getOptions();

    // get list of input help buttons
    this.getTriggers();

    // check for masking option overrides
    this.maskOptions = Object.assign({}, this.options.inputMaskOptionsExtend);

    // set character limits on text areas if defined
    this.getTextAreas();

    // Help button toggle ADA text.
    if (!this.importTriggered) {
      this.importTriggered = true;
      this.setHelpBttnText();
    } else {
      this.setHelpBttnText();
    }

    // Allow "optional" to be translated via the `global-params` JCRs.
    const optionalText = spartaGlobalFunctions.getGlobalParams('optionalText');
    if (optionalText) {
      const optionalTextEls = document.querySelectorAll('.spa-input-label__optional-text');
      for (let i = 0; i < optionalTextEls.length; i++) {
        optionalTextEls[i].innerHTML = optionalText;
      }
    }
    const element = [...document.querySelectorAll('.spa-input-select')];
    element.forEach(item => {
      item.addEventListener('change', event => {
        item.setAttribute('data-val', event.target.value);
      });
    });
    // start up
    this.boot({
      autoMask,
      widgetID
    });
  }

  /**
   * @method getInputs
   * @description find inputs within scope and return set this.inputs to that Array
   */
  getInputs() {
    // cache all inputs in case we need to iterate through them later
    this.inputs = [].slice.call(this.scope.getElementsByClassName('spa-input-box'));

    // iterate through inputs
    this.inputs.forEach(input => {
      const currencyInputs = input.querySelectorAll('.spa-input-prefix--currency');
      const inputValue = input.querySelector('.spa-input-text');
      // if we have currency input
      currencyInputs.forEach(currencyInput => {
        if (currencyInput?.hasAttribute('data-currency-value')) {
          const currencyType = currencyInput.getAttribute('data-currency-value');
          SpartaInputUtilityMethods.updateCurrencySymbol(inputValue, currencyType);
        }
      });
    });
  }

  /**
   * @method getCheckboxes
   * @description find checkbox and radio within scope and return set this.inputCheckboxes to that Array
   */
  getInputCheckboxes() {
    // cache all inputs in case we need to iterate through them later
    this.inputCheckboxes = [].slice.call(this.scope.getElementsByClassName('spa-input-option'));
    [...this.applicationContainer.querySelectorAll('.spa-fieldset')].forEach(this.updateButtons);
    this.inputCheckboxes.forEach(checkbox => {
      const fieldset = checkbox.closest('.spa-fieldset');
      if (fieldset) {
        const checkAllLink = fieldset.querySelectorAll('.checkbox-check-all-link');
        const unCheckAllLink = fieldset.querySelectorAll('.checkbox-uncheck-all-link');
        // Click function to check all checkboxes
        checkAllLink.forEach(button => {
          button.addEventListener('click', () => {
            [...fieldset.querySelectorAll('.spa-input-option--checkbox')].forEach(checkbox => {
              if (!checkbox.disabled) {
                checkbox.checked = true;
              }
            });
          });
        });
        // Click function to uncheck all checkboxes
        unCheckAllLink.forEach(button => {
          button.addEventListener('click', () => {
            [...fieldset.querySelectorAll('.spa-input-option--checkbox')].forEach(checkbox => {
              if (!checkbox.disabled) {
                checkbox.checked = false;
              }
            });
          });
        });
      }
    });
  }

  /**
   * @method updateButtons
   * @param fieldset target fieldset that contains the checkboxes
   * @description find the check/uncheckall button inside a fieldset and hides if checkboxes in a fieldset is one or all disabled
   */
  updateButtons(fieldset) {
    const checkboxes = fieldset.querySelectorAll('.spa-input-option--checkbox');
    const allDisabled = [...checkboxes].every(checkbox => checkbox.disabled);
    // Find buttons inside the same fieldset
    const checkboxControlWrapper = fieldset.querySelector('.checkbox-control-wrapper');
    // Hide check/uncheck all if there are no enabled checkoxes or one checkbox
    if (checkboxControlWrapper) {
      if (allDisabled || checkboxes.length === 1) {
        checkboxControlWrapper.style.display = 'none';
      } else {
        checkboxControlWrapper.style.display = '';
      }
    }
  }

  /**
   * @method getOptions
   * @description find options within scope and return set this.options to that Array
   */
  getOptions() {
    // cache all options in case we need to iterate through them later
    this.optionInputs = [].slice.call(this.scope.getElementsByClassName('spa-input-options-wrapper'));
  }

  /**
   * @method getTriggers
   * @description find input help buttons within scope and return set this.triggers to that Array
   */
  getTriggers() {
    this.triggers = [].slice.call(this.scope.getElementsByClassName('toggle-trigger')).concat([].slice.call(this.scope.getElementsByClassName('close-trigger')));
  }

  /**
   * @method getTextAreas
   * @description iterates over text area inputs and calls counter function
   * for those that contain .spar-char-count
   */
  getTextAreas() {
    this.inputs.filter(input => input.classList.contains('spa-input-box--textarea') && input.querySelector('.spa-char-count')).forEach(input => {
      this.setCharCounter(input);
    });
  }

  /**
   * @method setHelpBttnText
   * @description sets ADA text for help buttons
   */
  setHelpBttnText() {
    const {
      inputText
    } = this;
    this.triggers.forEach(trigger => {
      const label = trigger.getAttribute('aria-label');
      if (!label.includes(inputText.openHelp) && !label.includes(inputText.closeHelp)) {
        if (trigger.classList.contains('toggle-trigger')) {
          trigger.setAttribute('aria-label', inputText.openHelp.concat(label));
        } else if (trigger.classList.contains('close-trigger')) {
          // eslint-disable-next-line no-unused-expressions
          inputText.closeHelpADALabel ? trigger.setAttribute('aria-label', inputText.closeHelpADALabel.concat(inputText.closeHelp.concat(label))) : trigger.setAttribute('aria-label', inputText.closeHelp.concat(label));
        }
      }
    });
  }

  /**
   * @method boot
   * @description set up formatting, conditional content, and any device-specific field options
   */
  boot(autoMask) {
    // configure fields with conditional content
    this.conditionalFields();

    // configure any device-specific input types
    this.changeInputTypes();

    // initialize the ADA framework
    spartaInputAdaUtility.init(this.inputs);
    spartaInputAdaUtility.init(this.inputCheckboxes);

    // Formatting
    // initialize the sparta-input-formatting-utility
    if (spartaInputFormattingUtility) {
      spartaInputFormattingUtility.init();
    }

    // GIS Obfuscation
    // initialize the sparta-masking-utility
    spartaMaskingUtility.init(autoMask);
  }

  /**
   * @method conditionalFields
   * @description iterate through inputs, find ones with conditional content, inject content
   */
  conditionalFields() {
    // iterate through inputs
    this.optionInputs.forEach(container => {
      // if we have conditional content
      if (container.hasAttribute('data-option-has-conditional-content')) {
        // create temporary DOM fragment to store conditional content
        const conditionalContentFragment = document.createDocumentFragment();

        // get conditional content element's ID
        const conditionalContentContainerString = container.getAttribute('data-conditional-content');

        // get conditional content DOM node
        const conditionalContentOriginalContainer = document.getElementById(conditionalContentContainerString);

        // Skip this container if the specifified conditional content is not actually on the page.
        if (!conditionalContentOriginalContainer) {
          return;
        }
        conditionalContentOriginalContainer.classList.add('spa-input-conditional-content-wrapper');
        conditionalContentOriginalContainer.classList.remove('spa-input-conditional-content-container');

        // append conditional content DOM node to temporary DOM fragment
        conditionalContentFragment.appendChild(conditionalContentOriginalContainer);

        // check to see if conditional content container exists
        let conditionalContentNewContainer = container.querySelector('data-option-conditional-content');

        // if conditional content container does not exist, create it
        if (!conditionalContentNewContainer) {
          // create element
          conditionalContentNewContainer = document.createElement('div');

          // add attributes
          conditionalContentNewContainer.setAttribute('data-option-conditional-content', true);
          conditionalContentNewContainer.classList.add('spa-input-option-conditional-content');

          // append element to our container
          container.appendChild(conditionalContentNewContainer);
        }

        // inject DOM fragment into conditional content container
        conditionalContentNewContainer.appendChild(conditionalContentFragment);
      }
    });
  }

  /**
   * @method changeInputTypes
   * @description we want to use the native HTML 5 input types for some mobile inputs,
   *  but these inputs can have undesireable effects in some non-mobile browsers,
   *  because of this we only want to swap types dynamically at runtime
   */
  changeInputTypes() {
    // iterate through inputs
    this.inputs.forEach(container => {
      // check to see if input has a dynamic mobile type or pattern
      const input = container.querySelector('[data-mobile-input-type], [data-mobile-pattern]');

      // if we have device-specific types
      if (input) {
        // initialize variables for holding our type and pattern
        // these are currently the only two attributes we change based on device
        let type;
        let pattern;

        // get device-specific type and pattern
        if (this.isMobile) {
          type = input.getAttribute('data-mobile-input-type');
          pattern = input.getAttribute('data-mobile-pattern');
        } else {
          type = input.getAttribute('data-default-input-type');
          pattern = input.getAttribute('data-default-pattern');
        }

        // set new type if it is different, check to make sure type exists
        if (type && input.getAttribute('type') !== type) {
          input.setAttribute('type', type);
        }

        // set new pattern if it is different
        if (pattern && input.getAttribute('pattern') !== pattern) {
          input.setAttribute('pattern', pattern);
        }
      }
    });
  }

  /**
   * @method setCharCounter
   * @param {Object} elementObj container of text area that character count is applied to
   * @description initializes character counter for sparta text areas
   */
  setCharCounter(elementObj) {
    if (typeof elementObj !== 'undefined' && elementObj) {
      const charStore = {};
      charStore.el = elementObj.querySelector('.spa-input-textarea');
      const {
        el
      } = charStore;
      charStore.charNode = elementObj.querySelector('.spa-char');
      charStore.countNode = elementObj.querySelector('.spa-char-count');
      const {
        countNode
      } = charStore;
      charStore.warningNode = elementObj.querySelector('.spa-char-warning');
      charStore.hintNode = elementObj.querySelector('.spa-input-hint');
      charStore.maxLength = countNode.dataset.charLimit || 100;
      countNode.classList.add('spa-char-count--loaded');
      this.updateCharCounter(charStore);
      el.addEventListener('keypress', this.updateCharCounter.bind(this, charStore));
      el.addEventListener('keyup', this.updateCharCounter.bind(this, charStore));
      el.addEventListener('focus', this.updateCharCounter.bind(this, charStore));
      el.addEventListener('paste', () => {
        // Using setTimeout with 0 ms to allow asynchronous paste operation to complete
        setTimeout(() => this.updateCharCounter(charStore), 0);
      });
    }
  }

  /**
   * @method updateCharCounter
   * @param {Object} event event that is emitted from eventListener
   * @param {Object} charStore stores charater count in text area
   * @description updates character count in text area
   */
  updateCharCounter(charStore, event) {
    // Update current character count on keyup
    const {
      maxLength,
      el,
      countNode,
      warningNode,
      charNode,
      hintNode
    } = charStore;
    if (el.scrollHeight > el.clientHeight) {
      countNode.classList.add('spa-char-count--scrollbar');
    } else if (el.scrollHeight <= el.clientHeight && countNode.classList.contains('scrollbar')) {
      countNode.classList.remove('scrollbar');
    }

    // Enter should not be counted.
    if (event && event.keyCode === 13) {
      return;
    }
    const charRemainingText = this.inputText.textareaCharRemaining || 'characters remaining.';
    countNode.innerHTML = `${maxLength - parseInt(el.value.length, 10)} ${charRemainingText}`;
    if (el.value.length >= maxLength - 20) {
      countNode.classList.remove('spa-char-count--normal');
      countNode.classList.add('spa-char-count--overlimit');
      // remove hint id from aria-describedby if there and add it
      const retAria = el.getAttribute('aria-describedby') ? `${charNode.id} ${el.getAttribute('aria-describedby').replace(charNode.id, '').trim()}` : charNode.id.trim();
      el.setAttribute('aria-describedby', retAria.trim());
    } else {
      countNode.classList.remove('spa-char-count--overlimit');
      countNode.classList.add('spa-char-count--normal');
      if (el.getAttribute('aria-describedby')) {
        const retAria = el.getAttribute('aria-describedby').replace(charNode.id, '');
        el.setAttribute('aria-describedby', retAria);
      }
    }
    warningNode.innerHTML = el.value.length > maxLength ? this.inputText.textareaLimitExceeded || 'Limit exceeded.' : '';
    if (event && event.type === 'focus') {
      // remove hint id from aria-describedby if there and add it
      let retAria = el.getAttribute('aria-describedby') ? `${el.getAttribute('aria-describedby').replace(hintNode.id, '').trim()} ${hintNode.id}` : hintNode.id;
      retAria = retAria.trim();
      el.setAttribute('aria-describedby', retAria.trim());
    }
    if (el.getAttribute('aria-describedby')) {
      const ariaDescByAry = el.getAttribute('aria-describedby').split(' ');
      if (ariaDescByAry.includes('commentTextarea_errorMessage')) {
        // put error message first
        ariaDescByAry.splice(ariaDescByAry.indexOf('commentTextarea_errorMessage'), 0, ariaDescByAry.shift());
      }
      el.setAttribute('aria-describedby', ariaDescByAry.join(' '));
    }
  }
}
$.fn.bindHelp = function bindHelp() {
  const $elements = $(this);
  const namespace = 'inputHelp';
  return $elements.each(function iterateThroughElements() {
    const $trigger = $(this);
    const sInputId = $trigger.attr('id').replace('help-open-', '');
    const $targetHelpSection = $(`#helpLayer_${sInputId}`);
    const $targetHelpClose = $(`#help-close-${sInputId}`);

    // Setting up event binds...
    $trigger.off(`click.${namespace}`);
    $trigger.on(`click.${namespace}`, () => {
      $targetHelpSection.slideToggle(150, () => {
        if ($targetHelpSection.is(':visible')) {
          $targetHelpSection.attr('aria-hidden', 'false');
          $targetHelpClose.attr('aria-hidden', 'false');
          $targetHelpClose.focus();
        } else {
          $targetHelpSection.attr('aria-hidden', 'true');
          $targetHelpClose.attr('aria-hidden', 'true');
        }
      });
    });
    $targetHelpClose.off(`click.${namespace}`);
    $targetHelpClose.on(`click.${namespace}`, () => {
      $targetHelpSection.slideUp(150, () => {
        $targetHelpClose.attr('aria-hidden', 'true');
        $targetHelpSection.attr('aria-hidden', 'true');
        $trigger.focus();
      });
    });
  });
};
$.fn.disableCopyAndPaste = function disableCopyAndPaste() {
  const namespace = 'disableCopyAndPaste';
  $(this).on(`cut.${namespace} copy.${namespace} paste.${namespace}`, e => {
    e.preventDefault();
  });
};

// export SpartaInputUtility methods
const SpartaInputUtilityMethods = {
  updateCurrencySymbol(inputValue, currencyType) {
    return updateCurrencySymbol(inputValue, currencyType);
  }
};

// export our jQuery plugin
var index = $.fn.spartaInputs = function spartaInputs(settings = {}, element) {
  const target = element || this;

  // trigger input help
  $(target).find('.toggle-trigger, .toggle-trigger-link').bindHelp();

  // gis requirement, disable copy and paste for ssn fields
  $(target).find('[data-sparta-input-format=ssn]').disableCopyAndPaste();

  // return each element in the jQuery collection to allow jQuery method chaining
  return $(target).each(() => {
    new SpartaInputUtility($(target), settings);
  });
};
$.fn.destroySpartaInputs = function () {
  // TBD - destroy all the event handlers that we have added on the elements under the container
};

export { SpartaInputUtilityMethods, index as default };
